import { useMemo, useState } from 'react';
import { isArray, isDefined, isObject } from '../../../utils/isDefined';
import Optional from '../../../utils/optional';
import DefaultDetailRow from './DefaultDetailRow';
import TelepoUserDetails from './TelepoUserDetails';
import SubscriptionDetails from './SubscriptionDetails';
import { translate } from '../../../i18n/I18nProvider';
import {
  translateLabel,
  translateIdentifier
} from '../actions/translateActions';

const VALUES_TO_SHOW = [
  'action',
  'identifier',
  'configuration',
  'date_of_change',
  'account',
  'name',
  'referred_type',
  'product',
  'product_offering',
  'first_name',
  'last_name',
  'username',
  'email',
  'language',
  'sku',
  'name',
  'delivery_address',
  'product_characteristics'
];

const AccordionSection = ({
  label,
  children,
  defaultOpen = false,
  classes
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div>
      <div
        onClick={() => setIsOpen(o => !o)}
        className={classes.accordionSection}
      >
        <span style={{ fontSize: 24 }}>{isOpen ? '▾' : '▸'}</span>
        <span>{label}</span>
      </div>
      {isOpen && <div style={{ paddingLeft: 10 }}>{children}</div>}
    </div>
  );
};

const SpecificationDetails = ({
  data,
  label = '',
  classes,
  level = 0,
  accountName,
  client,
  shouldShowAll = false,
  identifier = ''
}) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [adminShowFull, setAdminShowFull] = useState(false);
  const specs = useMemo(() => {
    if (level > 0) return null;
    const spec = data?.[0]?.specification;
    return isArray(spec) ? spec : null;
  }, [data, level]);

  let specification = useMemo(() => {
    if (level > 0) {
      return data;
    }
    const spec = data?.[0]?.specification;

    if (isArray(spec)) {
      return Optional(spec[activeTabIndex]).or({});
    }

    if (isObject(spec)) {
      return spec;
    }

    return {};
  }, [data, activeTabIndex]);

  const currentIdentifier =
    level === 0 ? specification?.identifier || identifier : identifier;

  const showAll = useMemo(() => {
    if (level === 0) {
      const spec = data?.[0]?.specification;
      return isObject(spec) && !isArray(spec);
    }
    return shouldShowAll;
  }, [level, data, shouldShowAll]);

  const effectiveShowAll =
    level === 0 && !isDefined(client) ? adminShowFull : showAll;

  const sortedKeys = Object.keys(specification).sort((a, b) => {
    const aIsObject = isObject(specification[a]);
    const bIsObject = isObject(specification[b]);

    if (aIsObject === bIsObject) return 0;
    return aIsObject ? 1 : -1;
  });

  return (
    <div
      style={{
        paddingLeft: 10
      }}
    >
      <div>{label}</div>
      {level === 0 && !isDefined(client) && (
        <div className={classes.viewButtonsContainer}>
          <button
            onClick={() => setAdminShowFull(false)}
            className={classes.viewButton}
            style={{
              background: !adminShowFull ? '#333' : '#eee',
              color: !adminShowFull ? '#fff' : '#333'
            }}
          >
            Basic
          </button>
          <button
            onClick={() => setAdminShowFull(true)}
            className={classes.viewButton}
            style={{
              background: adminShowFull ? '#333' : '#eee',
              color: adminShowFull ? '#fff' : '#333'
            }}
          >
            Full
          </button>
        </div>
      )}
      {specs && specs.length > 1 && (
        <div className={classes.specContainer}>
          {specs.map((item, i) => (
            <button
              key={i}
              onClick={() => setActiveTabIndex(i)}
              className={classes.specButton}
              style={{
                borderBottom:
                  activeTabIndex === i
                    ? '2px solid #333'
                    : '2px solid transparent',
                fontWeight: activeTabIndex === i ? 'bold' : 'normal'
              }}
            >
              {translateIdentifier(item?.identifier) ||
                item?.identifier ||
                `${i + 1}`}
            </button>
          ))}
        </div>
      )}
      {sortedKeys.map((el, i) => {
        if (!effectiveShowAll && !VALUES_TO_SHOW.includes(el)) {
          return null;
        }

        const displayLabel = !effectiveShowAll ? translateLabel(el) : el;

        if (isArray(specification[el])) {
          return (
            <AccordionSection key={i} label={displayLabel} classes={classes}>
              {specification[el].map((item, idx) => {
                if (isObject(item)) {
                  const itemIdentifier = item?.identifier || currentIdentifier;

                  return (
                    <div key={idx} style={{ paddingLeft: 20, marginTop: 4 }}>
                      {Object.keys(item).map((key, keyIdx) => (
                        <div key={keyIdx} className={classes.innerRowContainer}>
                          <div>{key}</div>
                          <div className={classes.value}>
                            {typeof item[key] === 'boolean'
                              ? translate(item[key].toString().toUpperCase())
                              : item[key]}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                }
                return (
                  <div key={idx} className={classes.innerRowContainer}>
                    <div className={classes.arrayValue}>{item}</div>
                  </div>
                );
              })}
            </AccordionSection>
          );
        }

        if (isObject(specification[el])) {
          const item = specification[el];
          const itemIdentifier = item?.identifier || currentIdentifier;

          if (
            !effectiveShowAll &&
            el === 'configuration' &&
            itemIdentifier === 'SRV_TELEPO_USER_V1'
          ) {
            return (
              <AccordionSection
                key={i}
                label={displayLabel}
                classes={classes}
                defaultOpen={true}
              >
                <TelepoUserDetails item={item} classes={classes} el={el} />
              </AccordionSection>
            );
          }
          if (
            !effectiveShowAll &&
            el === 'configuration' &&
            itemIdentifier === 'SRV_QUOTE_SUBSCRIPTION_V1'
          ) {
            return (
              <AccordionSection
                key={i}
                label={displayLabel}
                classes={classes}
                defaultOpen={true}
              >
                <SubscriptionDetails
                  item={item}
                  classes={classes}
                  el={el}
                  accountName={accountName}
                />
              </AccordionSection>
            );
          }

          return (
            <AccordionSection key={i} label={displayLabel} classes={classes}>
              <SpecificationDetails
                data={specification[el]}
                label=""
                classes={classes}
                level={level + 1}
                accountName={accountName}
                client={client}
                shouldShowAll={effectiveShowAll}
                identifier={currentIdentifier}
              />
            </AccordionSection>
          );
        }

        return (
          <DefaultDetailRow
            key={i}
            label={displayLabel}
            dataKey={el}
            value={specification[el]}
            translateValues={!effectiveShowAll}
            classes={classes}
            accountName={accountName}
          />
        );
      })}
    </div>
  );
};

export default SpecificationDetails;
